import { __decorate } from "tslib";
import { ArrayDecoder, AutoEncoder, BooleanDecoder, EnumDecoder, field, StringDecoder } from '@simonbackx/simple-encoding';
import { SimpleError } from '@simonbackx/simple-errors';
import { v4 as uuidv4 } from 'uuid';
import { ResolutionRequest } from '../../files/ResolutionRequest.js';
import { PropertyFilter } from '../../filters/PropertyFilter.js';
import { getPermissionLevelNumber, PermissionLevel } from '../../PermissionLevel.js';
import { TranslatedString } from '../../TranslatedString.js';
export var RecordType;
(function (RecordType) {
    /**
     * Show a checkbox to the user so they can check the value on/off
     * It is possible to add a textarea as soon as the checkbox is selected if the extra property is set.
     */
    RecordType["Checkbox"] = "Checkbox";
    /**
     * Select one (or none if not required)
     */
    RecordType["ChooseOne"] = "ChooseOne";
    /**
     * Select one, zero or more from a menu
     * The way this is shown will differ depending on the minimum or maximum setting. e.g. when exactly one
     * item should be selected, we could show it with a dropdown menu
     */
    RecordType["MultipleChoice"] = "MultipleChoice";
    /**
     * Small text input
     */
    RecordType["Text"] = "Text";
    /**
     * Big text input
     */
    RecordType["Textarea"] = "Textarea";
    /**
     * Address input
     */
    RecordType["Address"] = "Address";
    RecordType["Phone"] = "Phone";
    RecordType["Email"] = "Email";
    RecordType["Date"] = "Date";
    RecordType["Price"] = "Price";
    RecordType["Image"] = "Image";
    RecordType["File"] = "File";
    RecordType["Integer"] = "Integer";
})(RecordType || (RecordType = {}));
export var FileType;
(function (FileType) {
    FileType["PDF"] = "PDF";
    FileType["Word"] = "Word";
    FileType["Excel"] = "Excel";
})(FileType || (FileType = {}));
export function getRecordTypeName(type) {
    var _a, _b;
    const all = [
        {
            value: RecordType.Text,
            name: $t(`%111`),
        },
        {
            value: RecordType.Textarea,
            name: $t(`%112`),
        },
        {
            value: RecordType.Address,
            name: $t(`%Cn`),
        },
        {
            value: RecordType.Email,
            name: $t(`%1FK`),
        },
        {
            value: RecordType.Phone,
            name: $t(`%wD`),
        },
        {
            value: RecordType.Date,
            name: $t(`%7R`),
        },
        {
            value: RecordType.Checkbox,
            name: $t(`%115`),
        },
        {
            value: RecordType.ChooseOne,
            name: $t(`%116`),
        },
        {
            value: RecordType.MultipleChoice,
            name: $t(`%117`),
        },
        {
            value: RecordType.File,
            name: $t(`%yU`),
        },
    ];
    return (_b = (_a = all.find(t => t.value === type)) === null || _a === void 0 ? void 0 : _a.name) !== null && _b !== void 0 ? _b : $t(`%Gr`);
}
export var RecordWarningType;
(function (RecordWarningType) {
    RecordWarningType["Info"] = "Info";
    RecordWarningType["Warning"] = "Warning";
    RecordWarningType["Error"] = "Error";
})(RecordWarningType || (RecordWarningType = {}));
export class RecordWarning extends AutoEncoder {
    constructor() {
        super(...arguments);
        this.text = TranslatedString.create();
        this.type = RecordWarningType.Info;
        /**
         * Show a warning if the associated value is falsy
         */
        this.inverted = false;
    }
    static get sort() {
        return (warning1, warning2) => {
            const priority1 = warning1.type;
            const priority2 = warning2.type;
            if ((priority1 === RecordWarningType.Error && priority2 === RecordWarningType.Warning)
                || (priority1 === RecordWarningType.Warning && priority2 === RecordWarningType.Info)
                || (priority1 === RecordWarningType.Error && priority2 === RecordWarningType.Info)) {
                return -1;
            }
            else if ((priority1 === RecordWarningType.Info && priority2 === RecordWarningType.Warning)
                || (priority1 === RecordWarningType.Warning && priority2 === RecordWarningType.Error)
                || (priority1 === RecordWarningType.Info && priority2 === RecordWarningType.Error)) {
                return 1;
            }
            else {
                return 0;
            }
        };
    }
    get icon() {
        switch (this.type) {
            case RecordWarningType.Error: return ' exclamation-two red';
            case RecordWarningType.Warning: return ' exclamation yellow';
            case RecordWarningType.Info: return ' info-text';
        }
    }
}
__decorate([
    field({ decoder: StringDecoder, defaultValue: () => uuidv4() })
], RecordWarning.prototype, "id", void 0);
__decorate([
    field({ decoder: StringDecoder }),
    field(TranslatedString.field({ version: 370 }))
], RecordWarning.prototype, "text", void 0);
__decorate([
    field({ decoder: new EnumDecoder(RecordWarningType) })
], RecordWarning.prototype, "type", void 0);
__decorate([
    field({ decoder: BooleanDecoder })
], RecordWarning.prototype, "inverted", void 0);
export class RecordChoice extends AutoEncoder {
    constructor() {
        super(...arguments);
        this.name = TranslatedString.create();
        this.description = TranslatedString.create();
        /**
         * Show a warning if selected (or not selected if inverted)
         */
        this.warning = null;
    }
}
__decorate([
    field({ decoder: StringDecoder, defaultValue: () => uuidv4() })
], RecordChoice.prototype, "id", void 0);
__decorate([
    field({ decoder: StringDecoder }),
    field(TranslatedString.field({ version: 370 }))
], RecordChoice.prototype, "name", void 0);
__decorate([
    field({ decoder: StringDecoder, version: 118 }),
    field(TranslatedString.field({ version: 370 }))
], RecordChoice.prototype, "description", void 0);
__decorate([
    field({ decoder: RecordWarning, version: 122, nullable: true })
], RecordChoice.prototype, "warning", void 0);
export class BaseRecordSettings extends AutoEncoder {
    constructor() {
        super(...arguments);
        /**
         * Short name (used mainly for displaying the information)
         */
        this.name = TranslatedString.create();
    }
}
__decorate([
    field({ decoder: StringDecoder, defaultValue: () => uuidv4() })
], BaseRecordSettings.prototype, "id", void 0);
__decorate([
    field({ decoder: StringDecoder }),
    field(TranslatedString.field({ version: 370 }))
], BaseRecordSettings.prototype, "name", void 0);
export class RecordSettings extends BaseRecordSettings {
    constructor() {
        super(...arguments);
        /**
         * When used with checkbox: checkbox needs to get checked (e.g accept terms, confirm age...)
         * Multiple choice: minimum one selection required
         * Text: required input
         */
        this.required = true;
        /**
         * Whether you need permission to collect this information
         */
        this.sensitive = false;
        /**
         * Only used for checkboxes
         */
        this.askComments = false;
        this.type = RecordType.Text;
        /**
         * In case of multiple choice: the values you can choose from with optional additional information
         */
        this.choices = [];
        /**
         * Label used for input (depending on the type)
         * Checkbox: text next to checkbox
         * Text inputs: label field above the input
         * If empty: name is used
         */
        this.label = TranslatedString.create();
        /**
         * Text underneath the label in case of a checkbox.
         * For other types: below the input
         */
        this.description = TranslatedString.create();
        /// In case of textboxes or comments for checked checkboxes
        this.inputPlaceholder = TranslatedString.create();
        /// Text below the input field for comments (if any)
        this.commentsDescription = TranslatedString.create();
        /**
         * Show a warning if selected / entered (or not selected/entered if inverted)
         */
        this.warning = null;
        this.externalPermissionLevel = PermissionLevel.Write;
        this.filter = null;
    }
    getDiffValue() {
        const type = getRecordTypeName(this.type);
        if (this.required) {
            return $t(`%Qk`) + ' ' + type;
        }
        return type;
    }
    validate(answers, requiredCategory = true) {
        const answer = answers.get(this.id);
        if (!requiredCategory && (!answer || answer.isEmpty)) {
            // Okay to skip
            return;
        }
        if (this.required && !answer) {
            throw new SimpleError({
                code: 'invalid_field',
                message: $t(`%qt`),
                field: this.id,
            });
        }
        if (answer) {
            if (answer.settings.type !== this.type) {
                throw new SimpleError({
                    code: 'field_type_changed',
                    message: $t(`%1Cu`),
                    field: this.id,
                });
            }
            answer.settings = this;
            answer.validate();
        }
    }
    checkPermissionForUserManager(level) {
        return getPermissionLevelNumber(this.externalPermissionLevel) >= getPermissionLevelNumber(level);
    }
    get excelColumns() {
        if (this.type === RecordType.Address) {
            let prefix = '';
            const defaultCategory = $t('%Cn');
            if (this.name.toString() !== defaultCategory) {
                prefix = this.name + ' - ';
            }
            return [
                {
                    name: prefix + $t('%Co'),
                    defaultCategory, // Ignore this name
                    width: 40,
                },
                {
                    name: prefix + $t('%cH'),
                    defaultCategory, // Ignore this name
                    width: 20,
                },
                {
                    name: prefix + $t('%c'),
                    defaultCategory, // Ignore this name
                    width: 20,
                },
                {
                    name: prefix + $t('%CQ'),
                    defaultCategory, // Ignore this name
                    width: 20,
                },
                {
                    name: prefix + $t('%Cp'),
                    defaultCategory, // Ignore this name
                    width: 20,
                },
            ];
        }
        let width = 20;
        switch (this.type) {
            case RecordType.Image:
            case RecordType.File:
                width = 115;
                break;
            case RecordType.Textarea:
                width = 80;
                break;
            case RecordType.Email:
                width = 40;
                break;
        }
        return [{
                name: this.name.toString(),
                width,
            }];
    }
    isEnabled(filterValue, options) {
        var _a;
        if (options === null || options === void 0 ? void 0 : options.level) {
            const level = options === null || options === void 0 ? void 0 : options.level;
            if (!this.checkPermissionForUserManager(level)) {
                return false;
            }
        }
        if (options === null || options === void 0 ? void 0 : options.additionalFilter) {
            if (!options.additionalFilter(this)) {
                return false;
            }
        }
        if (this.filter && !((_a = this.filter) === null || _a === void 0 ? void 0 : _a.isEnabled(filterValue))) {
            return false;
        }
        return !!filterValue.isRecordEnabled(this);
    }
    duplicate() {
        const c = this.clone();
        c.id = uuidv4();
        return c;
    }
}
__decorate([
    field({ decoder: BooleanDecoder })
], RecordSettings.prototype, "required", void 0);
__decorate([
    field({ decoder: BooleanDecoder, version: 123 })
], RecordSettings.prototype, "sensitive", void 0);
__decorate([
    field({ decoder: BooleanDecoder, version: 119 })
], RecordSettings.prototype, "askComments", void 0);
__decorate([
    field({ decoder: new EnumDecoder(RecordType) })
], RecordSettings.prototype, "type", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(RecordChoice) })
], RecordSettings.prototype, "choices", void 0);
__decorate([
    field({ decoder: StringDecoder }),
    field(TranslatedString.field({ version: 370 }))
], RecordSettings.prototype, "label", void 0);
__decorate([
    field({ decoder: StringDecoder }),
    field(TranslatedString.field({ version: 370 }))
], RecordSettings.prototype, "description", void 0);
__decorate([
    field({ decoder: StringDecoder }),
    field(TranslatedString.field({ version: 370 }))
], RecordSettings.prototype, "inputPlaceholder", void 0);
__decorate([
    field({ decoder: StringDecoder, version: 120 }),
    field(TranslatedString.field({ version: 370 }))
], RecordSettings.prototype, "commentsDescription", void 0);
__decorate([
    field({ decoder: RecordWarning, version: 122, nullable: true })
], RecordSettings.prototype, "warning", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(ResolutionRequest), optional: true })
], RecordSettings.prototype, "resolutions", void 0);
__decorate([
    field({ decoder: new EnumDecoder(FileType), nullable: true, optional: true })
], RecordSettings.prototype, "fileType", void 0);
__decorate([
    field({ decoder: new EnumDecoder(PermissionLevel), version: 356 })
], RecordSettings.prototype, "externalPermissionLevel", void 0);
__decorate([
    field({ decoder: PropertyFilter, version: 365, nullable: true, optional: true })
], RecordSettings.prototype, "filter", void 0);
//# sourceMappingURL=RecordSettings.js.map